import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { TYPE_QUESTIONS, TOTAL_ONB_STEPS, deriveIdealClient } from "@/lib/onboardingConfig";
import { Constellation } from "@/components/onboarding/Constellation";
import { readMarket } from "@/components/onboarding/MarketField";
import { newProfile, deriveProfile, isEmptyProfile } from "@/components/onboarding/profiles";
import { TYPE_META } from "@/lib/format";
import { Step1, Step2, Step3, Step4, Step5, Step6 } from "@/components/onboarding/OnbSteps";
import { Loader2 } from "lucide-react";
import "@/styles/onboarding.css";

const DEFAULTS = {
  experience_level: "", business_type: "",
  business_name: "", offer: "", ideal_client: "", problem: "", market: "", market_scope: "", market_countries: [],
  goals: "", current_acquisition: "", time_availability: "", message_preference: "", profile_mode: "auto",
  networks: [], networks_other: "", networks_from_scratch: [],
  questionnaire: {}, goals_list: [], priority_offers: "",
  avatars_list: [], onboarding_completed: false, onboarding_step: 1,
};

// P2-L: el nombre del perfil no se pide; se autogenera una etiqueta útil. Nunca se usa como query.
export const autoAvatarName = (a) => (a.name || "").trim() || [TYPE_META[a.type]?.label, a.sector].filter(Boolean).join(" · ") || "Perfil";

export default function Onboarding({ onDone }) {
  const [loaded, setLoaded] = useState(false);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(false);
  const [errors, setErrors] = useState({});
  const [biz, setBiz] = useState(DEFAULTS);
  const timer = useRef(null);
  const skipSave = useRef(true);
  const done = useRef(false);   // tras confirmar, ningún borrador pendiente puede pisar el guardado final
  const top = useRef(null);

  useEffect(() => {
    api.get("/business-brain").then((r) => {
      const data = r.data || {};
      const merged = { ...DEFAULTS, ...data };
      merged.questionnaire = data.questionnaire || {};
      merged.networks = data.networks || [];
      merged.networks_from_scratch = data.networks_from_scratch || [];
      merged.goals_list = data.goals_list || [];
      merged.avatars_list = data.avatars_list || [];
      const m = readMarket(merged);
      if (!data.market_scope && m.scope) { merged.market_scope = m.scope; merged.market_countries = m.countries; }
      setBiz(merged);
      setStep(Math.min(Math.max(data.onboarding_step || 1, 1), TOTAL_ONB_STEPS));
      setLoaded(true);
    }).catch(() => { setBiz(DEFAULTS); setLoaded(true); });
  }, []);

  // Guardado automático (borrador) al cambiar datos o de paso.
  useEffect(() => {
    if (!loaded || done.current) return;
    if (skipSave.current) { skipSave.current = false; return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.put("/business-brain", { ...biz, onboarding_step: step, onboarding_completed: false })
        .then(() => { setSavedAt(true); setTimeout(() => setSavedAt(false), 1500); })
        .catch(() => {});
    }, 700);
    return () => timer.current && clearTimeout(timer.current);
  }, [biz, step, loaded]);

  useEffect(() => { top.current?.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  const clearErr = (k) => { if (errors[k]) setErrors((e) => ({ ...e, [k]: "" })); };
  const setB = (k, v) => { setBiz((f) => ({ ...f, [k]: v })); clearErr(k); };
  const patch = (p) => { setBiz((f) => ({ ...f, ...p })); if ("market" in p) clearErr("market"); };
  const setQ = (k, v) => { setBiz((f) => ({ ...f, questionnaire: { ...f.questionnaire, [k]: v } })); clearErr(k); };
  const toggleArr = (key, val) => { setBiz((f) => {
    const arr = f[key] || [];
    return { ...f, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
  }); clearErr(key); };
  const updateAvatar = (p) => setBiz((f) => ({ ...f, avatars_list: (f.avatars_list || []).map((a) => (a.id === p.id ? deriveProfile(p) : a)) }));
  const addProfile = (goal) => setBiz((f) => ({ ...f, avatars_list: [...(f.avatars_list || []), deriveProfile(newProfile(goal))] }));
  const removeAvatar = (id) => setBiz((f) => ({ ...f, avatars_list: (f.avatars_list || []).filter((a) => a.id !== id) }));

  // HTML final: en modo manual hay una tarjeta por cada objetivo de búsqueda.
  const searchKey = (biz.goals_list || []).filter((g) => g !== "reactivar").join("|");
  useEffect(() => {
    if (!loaded || step !== 5 || biz.profile_mode !== "manual") return;
    const goals = searchKey ? searchKey.split("|") : [];
    setBiz((f) => {
      const list = f.avatars_list || [];
      const missing = goals.filter((g) => !list.some((a) => a.goal === g));
      return missing.length ? { ...f, avatars_list: [...list, ...missing.map((g) => deriveProfile(newProfile(g)))] } : f;
    });
  }, [loaded, step, biz.profile_mode, searchKey]);

  const validateStep = (s) => {
    const errs = {};
    if (s === 1 && !biz.experience_level) errs.experience_level = "Elige una opción para continuar.";
    if (s === 2 && !biz.business_type) errs.business_type = "Elige el modelo que mejor encaje.";
    if (s === 3) {
      if (!biz.offer.trim()) errs.offer = "Cuéntanos brevemente qué ofreces para poder adaptar el resto del recorrido.";
      (TYPE_QUESTIONS[biz.business_type] || []).forEach((q) => {
        if (q.required && !(biz.questionnaire[q.k] || "").trim()) errs[q.k] = "Este dato es esencial para entender tu actividad.";
      });
      const sc = biz.market_scope;
      if ((sc === "country" || sc === "multiple") && !(biz.market_countries || []).length) {
        errs.market = sc === "multiple" ? "Selecciona al menos un país para continuar." : "Selecciona un país para continuar.";
      }
    }
    if (s === 4 && (biz.goals_list || []).length === 0) errs.goals_list = "Selecciona al menos un objetivo para poder preparar los perfiles adecuados.";
    return errs;
  };

  const next = () => {
    const errs = validateStep(step);
    setErrors(errs);
    const keys = Object.keys(errs);
    if (keys.length) {
      toast.error("Revisa los campos marcados.");
      const map = { offer: "onb-offer", market: "onb-market-question" };
      const tid = map[keys[0]] || `onb-q-${keys[0]}`;
      setTimeout(() => document.querySelector(`[data-testid="${tid}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
      return;
    }
    setStep((s) => Math.min(s + 1, TOTAL_ONB_STEPS));
  };
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const confirm = async () => {
    if (saving) return;
    setSaving(true);
    done.current = true;
    if (timer.current) clearTimeout(timer.current);
    try {
      const avatars_list = (biz.avatars_list || []).filter((a) => !(a.auto_name && isEmptyProfile(a)));
      const ideal_client = (biz.ideal_client || "").trim() || deriveIdealClient(biz);
      await api.put("/business-brain", { ...biz, ideal_client, avatars_list, onboarding_step: TOTAL_ONB_STEPS, onboarding_completed: true });
      toast.success("¡Onboarding completado! Ahora aprueba el conocimiento de tu negocio.");
      onDone();
    } catch (err) {
      toast.error(apiError(err));
      done.current = false;
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <div className="onb-overlay" style={{ display: "grid", placeItems: "center" }}>
        <Loader2 className="animate-spin" style={{ color: "#E0B970" }} size={26} />
      </div>
    );
  }

  const props = { biz, setB, setQ, patch, toggleArr, errors, setStep };
  const last = step === TOTAL_ONB_STEPS;

  return (
    <div ref={top} data-testid="onboarding-overlay" className="onb-overlay">
      <div className="onboarding">
        <div className="onb-glow" />
        <Constellation />
        <header className="onb-head">
          <span className="onb-brand"><span className="logo-image" role="img" aria-label="CaptAcción" /></span>
          <small>Configuración inicial · Se guarda solo</small>
        </header>
        <main className="onb-wrap">
          <div className="onb-progress" data-testid="onb-progress">
            {Array.from({ length: TOTAL_ONB_STEPS }, (_, i) => <i key={i} className={i < step ? "done" : ""} />)}
          </div>
          <section className="onb-card glass" data-testid="onb-card">
            <div className="onb-meta">
              <span data-testid="onb-step-label">Paso {step} de {TOTAL_ONB_STEPS}</span>
              <span className="rating">● <span data-testid="onb-autosave">{last ? "Listo para revisar" : savedAt ? "Guardado ✓" : "Guardado"}</span></span>
            </div>
            <div key={step}>
              {step === 1 && <Step1 {...props} />}
              {step === 2 && <Step2 {...props} />}
              {step === 3 && <Step3 {...props} />}
              {step === 4 && <Step4 {...props} />}
              {step === 5 && <Step5 {...props} updateAvatar={updateAvatar} addProfile={addProfile} removeAvatar={removeAvatar} />}
              {step === 6 && <Step6 {...props} />}
            </div>
            <div className="onb-actions">
              <button type="button" className="btn btn-ghost" onClick={back} data-testid="onb-back" style={{ visibility: step === 1 ? "hidden" : "visible" }}>← Atrás</button>
              <span className="tiny">{last ? "Revisa la información antes de continuar" : "Puedes cambiar tus respuestas después"}</span>
              {last ? (
                <button type="button" className="btn btn-gold" onClick={confirm} disabled={saving} data-testid="onb-confirm">{saving ? "Guardando…" : "Confirmar y empezar"} <span className="arr">→</span></button>
              ) : (
                <button type="button" className="btn btn-gold" onClick={next} data-testid="onb-next">Continuar <span className="arr">→</span></button>
              )}
            </div>
          </section>
          <p className="onb-privacy">Tus respuestas se guardan en tu cuenta y solo se usan para adaptar CaptAcción a tu negocio.</p>
        </main>
      </div>
    </div>
  );
}
